import React, { useState, useEffect } from 'react';
import Modal from './Modal';
import { useLanguage } from '../../context/LanguageContext';

const DeletionConfirmModal = ({
    isOpen,
    onClose,
    onConfirm,
    title,
    message,
    itemName,
    loading = false,
    requireReason = true
}) => {
    const { t } = useLanguage();
    const [reason, setReason] = useState('');
    const [showReasonError, setShowReasonError] = useState(false);

    // Reset state when modal opens/closes
    useEffect(() => {
        if (isOpen) {
            setReason('');
            setShowReasonError(false);
        }
    }, [isOpen]);

    const handleConfirm = () => {
        if (requireReason && !reason.trim()) {
            setShowReasonError(true);
            return;
        }

        onConfirm(reason.trim());
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && e.ctrlKey) {
            handleConfirm();
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={title || t('confirm_deletion')}
            footer={(
                <>
                    <button
                        type="button"
                        className="inline-flex justify-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                        onClick={onClose}
                        disabled={loading}
                    >
                        {t('cancel')}
                    </button>
                    <button
                        type="button"
                        className="inline-flex justify-center px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:bg-gray-400 disabled:cursor-not-allowed"
                        onClick={handleConfirm}
                        disabled={loading || (requireReason && !reason.trim())}
                    >
                        {loading ? t('deleting') : t('delete')}
                    </button>
                </>
            )}
        >
            <div className="space-y-4">
                {/* Warning Message */}
                <div className="bg-red-50 border border-red-200 rounded-md p-3">
                    <div className="flex">
                        <div className="flex-shrink-0">
                            <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                            </svg>
                        </div>
                        <div className="ml-3">
                            <h3 className="text-sm font-medium text-red-800">
                                {t('warning')}
                            </h3>
                            <div className="mt-1 text-sm text-red-700">
                                <p>
                                    {message || `${t('are_you_sure_delete')} ${itemName}? ${t('action_cannot_undone')}`}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Reason Input */}
                {requireReason && (
                    <div>
                        <label htmlFor="deletion-reason" className="block text-sm font-medium text-gray-700 mb-2">
                            {t('reason_for_deletion')} <span className="text-red-500">*</span>
                        </label>
                        <textarea
                            id="deletion-reason"
                            rows={3}
                            className={`shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 rounded-md ${showReasonError ? 'border-red-300 ring-red-500' : ''
                                }`}
                            placeholder={t('enter_reason_placeholder')}
                            value={reason}
                            onChange={(e) => {
                                setReason(e.target.value);
                                if (showReasonError && e.target.value.trim()) {
                                    setShowReasonError(false);
                                }
                            }}
                            onKeyPress={handleKeyPress}
                            autoFocus
                            disabled={loading}
                        />
                        {showReasonError && (
                            <p className="mt-1 text-sm text-red-600">
                                {t('reason_required')}
                            </p>
                        )}
                        <p className="mt-1 text-xs text-gray-500">
                            {t('ctrl_enter_to_confirm')}
                        </p>
                    </div>
                )}

                {/* Item Details */}
                {itemName && (
                    <div className="bg-gray-50 rounded-md p-3">
                        <h4 className="text-sm font-medium text-gray-700 mb-1">
                            {t('item_to_delete')}:
                        </h4>
                        <p className="text-sm text-gray-600 font-mono">
                            {itemName}
                        </p>
                    </div>
                )}

                {/* Audit Trail Note */}
                <div className="text-xs text-gray-500 bg-blue-50 border border-blue-200 rounded-md p-2">
                    <p>
                        <strong>{t('note')}:</strong> {t('deletion_will_be_logged')}
                    </p>
                </div>
            </div>
        </Modal>
    );
};

export default DeletionConfirmModal;